import { put, call, takeLatest, type CallEffect, type PutEffect, type ForkEffect } from 'redux-saga/effects';
import { AnyAction } from 'redux';
import { FeedbackActionTypes } from './enums';
import { createFeedback, fetchAllFeedback, getFeedbackById, resolveFeedback, uploadDocument, activateDocument } from './api';
import { uploadFile } from '@utilities';

type FeedbackSagaEffect = Generator<CallEffect<any> | PutEffect<any>>;
type FeedbackSagaFormEffect = Generator<ForkEffect<void>>;

// ─── Create Feedback ──────────────────────────────────────────────────────────

export function* createFeedbackEffect(action: AnyAction): FeedbackSagaEffect {
	const { formData, files, successCB, errorCB, finalCB } = action.payload;
	try {
		let docId: number | undefined;

		// Upload + activate flow if files are provided
		if (files && files.length > 0) {
			const uploadRequest = {
				reference_id: String(Date.now()),
				reference_type: 'FEEDBACK',
				documents: [
					{
						category: 'FEEDBACK',
						sub_category: 'ATTACHMENT',
						attachments: files.map((f: File) => ({ file: f.name, cover_image: false })),
						document_id: 0
					}
				]
			};

			const { data: uploadData }: any = yield call(uploadDocument, uploadRequest);
			const responses = uploadData?.documents_upload_responses ?? uploadData?.documentsUploadResponses;
			if (responses?.length) {
				const docResponse = responses[0];
				const documentId = docResponse?.document_id ?? docResponse?.documentId;
				const attachments = docResponse?.attachments ?? [];

				// Upload each file to S3
				for (let i = 0; i < files.length; i++) {
					const uploadUrl = attachments[i]?.document_url ?? attachments[i]?.documentUrl;
					const fileName = attachments[i]?.file_name ?? attachments[i]?.fileName ?? files[i].name;
					if (uploadUrl) {
						yield call(uploadFile, uploadUrl, files[i], fileName);
					}
				}

				// Activate the document
				if (documentId != null) {
					yield call(activateDocument, { documentIds: [documentId] });
					docId = documentId;
				}
			}
		}

		// Create feedback with optional docId
		const feedbackPayload = { ...formData, ...(docId != null && { docId }) };
		const { data }: any = yield call(createFeedback, feedbackPayload);
		yield put({ type: FeedbackActionTypes.CREATE_FEEDBACK_SUCCESS, payload: data });
		if (successCB) successCB(data);
	} catch (error: any) {
		yield put({
			type: FeedbackActionTypes.CREATE_FEEDBACK_FAILURE,
			payload: error?.response?.data?.message ?? 'Failed to create feedback.'
		});
		if (errorCB) errorCB(error?.response?.data?.message ?? 'Failed to create feedback.');
	} finally {
		if (finalCB) finalCB();
	}
}

// ─── Fetch All Feedback ───────────────────────────────────────────────────────

export function* fetchAllFeedbackEffect(action: AnyAction): FeedbackSagaEffect {
	const { formData, successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchAllFeedback, formData);
		yield put({ type: FeedbackActionTypes.FETCH_ALL_FEEDBACK_SUCCESS, payload: data });
		if (successCB) successCB(data);
	} catch (error: any) {
		yield put({
			type: FeedbackActionTypes.FETCH_ALL_FEEDBACK_FAILURE,
			payload: error?.response?.data?.message ?? 'Failed to fetch feedback list.'
		});
		if (errorCB) errorCB(error?.response?.data?.message ?? 'Failed to fetch feedback list.');
	}
}

// ─── Get Feedback By ID ───────────────────────────────────────────────────────

export function* getFeedbackByIdEffect(action: AnyAction): FeedbackSagaEffect {
	const { feedbackId, successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getFeedbackById, feedbackId);
		yield put({ type: FeedbackActionTypes.GET_FEEDBACK_BY_ID_SUCCESS, payload: data });
		if (successCB) successCB(data);
	} catch (error: any) {
		yield put({
			type: FeedbackActionTypes.GET_FEEDBACK_BY_ID_FAILURE,
			payload: error?.response?.data?.message ?? 'Failed to fetch feedback.'
		});
		if (errorCB) errorCB(error?.response?.data?.message ?? 'Failed to fetch feedback.');
	}
}

// ─── Resolve Feedback ─────────────────────────────────────────────────────────

export function* resolveFeedbackEffect(action: AnyAction): FeedbackSagaEffect {
	const { feedbackId, successCB, errorCB, finalCB } = action.payload;
	try {
		yield call(resolveFeedback, feedbackId);
		yield put({ type: FeedbackActionTypes.RESOLVE_FEEDBACK_SUCCESS });
		if (successCB) successCB();
	} catch (error: any) {
		yield put({
			type: FeedbackActionTypes.RESOLVE_FEEDBACK_FAILURE,
			payload: error?.response?.data?.message ?? 'Failed to resolve feedback.'
		});
		if (errorCB) errorCB(error?.response?.data?.message ?? 'Failed to resolve feedback.');
	} finally {
		if (finalCB) finalCB();
	}
}

// ─── Individual Saga Watchers ────────────────────────────────────────────────

export function* createFeedbackSaga(): FeedbackSagaFormEffect {
	yield takeLatest(FeedbackActionTypes.CREATE_FEEDBACK_REQUEST, createFeedbackEffect);
}

export function* fetchAllFeedbackSaga(): FeedbackSagaFormEffect {
	yield takeLatest(FeedbackActionTypes.FETCH_ALL_FEEDBACK_REQUEST, fetchAllFeedbackEffect);
}

export function* getFeedbackByIdSaga(): FeedbackSagaFormEffect {
	yield takeLatest(FeedbackActionTypes.GET_FEEDBACK_BY_ID_REQUEST, getFeedbackByIdEffect);
}

export function* resolveFeedbackSaga(): FeedbackSagaFormEffect {
	yield takeLatest(FeedbackActionTypes.RESOLVE_FEEDBACK_REQUEST, resolveFeedbackEffect);
}
